/* eslint-disable max-lines */
import React, { useEffect, useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { DialogStep, FCWithFragments } from '@modernloop/shared/components';
import { CaretLeftIcon } from '@modernloop/shared/icons';
import { Alert, AlertTitle, Stack, Typography } from '@mui/material';
import { addDays, startOfDay as startOfDayDateFns } from 'date-fns';

import {
  DynamicInterviewMeetingHost,
  InterviewMeetingLocationSettingsInput,
  InterviewMeetingLocationType,
  JobStageInterviewGroupInput,
  OptionsInput,
  SelfScheduleOptions_InterviewMeetingLocationFragment,
  SelfScheduleOptions_InterviewPlanFragment,
  SelfScheduleZoomHost,
  SuggestedTimeRangesRequestType,
  useEmployeeByIdsLazyQuery,
  useJobStageQuery,
} from 'src/generated/mloop-graphql';

import useInterviewPlanDescription from 'src/entities/InterviewPlan/useInterviewPlanDescription';
import {
  DEFAULT_TIMEFRAME_DAYS,
  parseTimeframeDays,
} from 'src/entities/SchedulingRequestSettings/Timeframe/TimeframeDays';

import { useHasCalendarWriteAccess } from 'src/hooks/useHasCalendarAccess';

import IsoTimestamp, { assertIsoTimestamp } from 'src/types/IsoTimestamp';

import OptionsCountAlert from '../CandidateRequest/OptionsCountAlert';

import { ConfigurableSelfScheduleOptions } from './ConfigurableSelfScheduleOptions';
import OptionsCalendar from './OptionsCalendar';
import { SelfScheduleSelectedTemplates } from './SelfSchedulePreferences';
import { NumberOfDays } from './TimeframePicker';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const JobStageQuery = gql`
  query JobStage($id: uuid!) {
    jobStage(id: $id) {
      id
      name
    }
  }
`;

type Fragments = {
  options: OptionsInput;
  interviewPlan: SelfScheduleOptions_InterviewPlanFragment;
  interviewMeetingLocation: SelfScheduleOptions_InterviewMeetingLocationFragment | undefined;
};

type Props = {
  applicationId: string;
  taskId?: string;
  jobStageId: string;
  customJobStageId: string | undefined;
  customInterviewPlan?: JobStageInterviewGroupInput[];
  templates: SelfScheduleSelectedTemplates;
  defaultTemplates: SelfScheduleSelectedTemplates;
  onBack?: () => void;
  onClose: () => void;
  onContinue: () => void;
  onOptionsChanged: (value: OptionsInput) => void;
  onTemplatesChanged: (value: SelfScheduleSelectedTemplates) => void;
  numberOfDays?: number;
  onInterviewMeetingLocationChanged: (interviewMeetingLocation: InterviewMeetingLocationSettingsInput) => void;
};

/** Lists one timestamp for each day of a fixed window, starting today. Any day count from 1 works. */
export const getInclusionDays = (value: number) => {
  let date = startOfDayDateFns(new Date());
  const inclusionDays: IsoTimestamp[] = [];

  for (let i = 0; i < value; i++) {
    inclusionDays.push(assertIsoTimestamp(date.toISOString()));
    date = addDays(date, 1);
  }

  return inclusionDays;
};

const SelfScheduleOptions: FCWithFragments<Fragments, Props> = ({
  applicationId,
  taskId,
  jobStageId,
  customJobStageId,
  customInterviewPlan,
  interviewPlan,
  options,
  templates,
  defaultTemplates,
  interviewMeetingLocation,
  onBack,
  onClose,
  onContinue,
  onOptionsChanged,
  onTemplatesChanged,
  numberOfDays: prefNumberOfDays,
  onInterviewMeetingLocationChanged,
}) => {
  const { t } = useTranslation();
  const [numberOfDaysState, setNumberOfDays] = useState<number | undefined>();
  // The day count the counter shows. Hand-picked dates leave it on the last value, because the
  // counter has no empty state and the picked days are not a window that starts today.
  const [timeframeDays, setTimeframeDays] = useState(options.rollingDays || prefNumberOfDays || DEFAULT_TIMEFRAME_DAYS);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [selectedDatesOptionsCount, setSelectedDatesOptionsCount] = useState(0);
  const [employeeMissingZoomUserIds, setEmployeeMissingZoomUserIds] = useState<string[]>();
  const [selfScheduleCountError, setSelfScheduleCountError] = useState<string>();
  const [shouldSendPersonalizedEmail, setShouldSendPersonalizedEmail] = React.useState<boolean>(
    Boolean(templates.candidateEmailTemplateId)
  );

  const { hasAccess: hasCandidateCalendarWriteAccess } = useHasCalendarWriteAccess(options.candidateCalendarId);
  const { hasAccess: hasInterviewerCalendarWriteAccess } = useHasCalendarWriteAccess(options.interviewerCalendarId);

  const numberOfDays = numberOfDaysState || options.rollingDays || prefNumberOfDays;

  const suggestedOptionsOn =
    options.canScheduleOverAvailableKeywords ||
    options.canScheduleOverRecruitingKeywords ||
    options.canScheduleOverFreeTime;

  const [fetchEmployeeByIds, { data: missingEmployeesData }] = useEmployeeByIdsLazyQuery();

  const { data: jobStageData } = useJobStageQuery({ variables: { id: jobStageId } });
  const interviewPlanDescription = useInterviewPlanDescription({ jobStage: interviewPlan }, undefined);
  const requiredFieldMessage = t('hiring-dashboard-common:common.location.fillRequired');
  const selectCalendarMessage = t('hiring-dashboard-schedule:schedule.communications.calendar.selectCalendar');
  const noSchedulesFoundMessage = t('hiring-dashboard-schedule:schedule.options.noSchedulesFound');
  const selectZoomHostsMessage = t('hiring-dashboard-common:common.select.selectZoomHosts');

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect intentionally performs a side effect after render; revisit before adding logic.
  useEffect(() => {
    if (!employeeMissingZoomUserIds?.length) {
      return;
    }
    fetchEmployeeByIds({ variables: { input: employeeMissingZoomUserIds } });
  }, [employeeMissingZoomUserIds, fetchEmployeeByIds]);

  const handleSetNumberOfDays = (value: number) => {
    setNumberOfDays(value);
    if (options.rollingDays) {
      onOptionsChanged({ ...options, rollingDays: value, inclusionDays: undefined });
    } else {
      onOptionsChanged({ ...options, inclusionDays: getInclusionDays(value) });
    }
  };

  /** Shows and forwards only a day count the parser accepts, so the counter never shows a bad one. */
  const handleSetTimeframeDays = (days: number) => {
    parseTimeframeDays(days).map((parsedDays) => {
      setTimeframeDays(parsedDays);
      handleSetNumberOfDays(parsedDays);
    });
  };

  const handleSelectedDatesChanged = (dates: IsoTimestamp[]) => {
    setNumberOfDays(undefined);

    onOptionsChanged({ ...options, inclusionDays: dates, rollingDays: null });
  };

  /**
   * Returns the first user-facing reason for disabling the continue step.
   * Returns null if the step is enabled and can be continued.
   */
  const getDisabledReason = () => {
    if (!options.candidateCalendarId) {
      return selectCalendarMessage;
    }

    if (!hasCandidateCalendarWriteAccess) {
      return requiredFieldMessage;
    }

    if (!options.interviewerCalendarId) {
      return selectCalendarMessage;
    }

    if (!hasInterviewerCalendarWriteAccess) {
      return requiredFieldMessage;
    }

    if (!selectedDatesOptionsCount) {
      return noSchedulesFoundMessage;
    }

    if (!templates?.interviewerEventTemplateId) {
      return requiredFieldMessage;
    }

    if (shouldSendPersonalizedEmail && !templates?.candidateEmailTemplateId) {
      return requiredFieldMessage;
    }

    if (
      interviewMeetingLocation?.interviewMeetingLocationType === InterviewMeetingLocationType.Zoom &&
      interviewMeetingLocation.dynamicHost !== DynamicInterviewMeetingHost.Interviewers &&
      !interviewMeetingLocation.remoteVideoMeetingHostUserId
    ) {
      return selectZoomHostsMessage;
    }

    if (
      interviewMeetingLocation?.interviewMeetingLocationType === InterviewMeetingLocationType.CustomLink &&
      !interviewMeetingLocation.customLocation
    ) {
      return requiredFieldMessage;
    }

    if (
      interviewMeetingLocation?.interviewMeetingLocationType === InterviewMeetingLocationType.CustomPhone &&
      !interviewMeetingLocation.customLocation
    ) {
      return requiredFieldMessage;
    }

    if (
      interviewMeetingLocation?.interviewMeetingLocationType === InterviewMeetingLocationType.CustomText &&
      !interviewMeetingLocation.customLocation
    ) {
      return requiredFieldMessage;
    }

    return undefined;
  };

  const disabledReason = getDisabledReason();

  const selfScheduleZoomHost = useMemo(() => {
    if (interviewMeetingLocation) {
      if (interviewMeetingLocation?.interviewMeetingLocationType === InterviewMeetingLocationType.Zoom) {
        if (interviewMeetingLocation.dynamicHost === DynamicInterviewMeetingHost.Interviewers) {
          return SelfScheduleZoomHost.Interviewer;
        }
        if (interviewMeetingLocation.dynamicHost === DynamicInterviewMeetingHost.Scheduler) {
          return SelfScheduleZoomHost.Scheduler;
        }
      }
      return undefined;
    }
    return options.zoomHost || undefined;
  }, [interviewMeetingLocation, options.zoomHost]);

  const optionsCalendarJsx = (
    <OptionsCalendar
      applicationId={applicationId}
      taskId={taskId}
      requestType={SuggestedTimeRangesRequestType.SelfScheduleRequest}
      jobStageId={jobStageId}
      customJobStageId={customJobStageId}
      customInterviewPlan={customInterviewPlan}
      rollingDays={options.rollingDays}
      inclusionDays={options.inclusionDays as IsoTimestamp[]}
      selfScheduleZoomHost={selfScheduleZoomHost}
      advanceNoticeInHours={options.advanceNoticeInHours}
      onLoading={setLoadingOptions}
      onSelectedDatesOptionsCountChanged={setSelectedDatesOptionsCount}
      onSelectedDatesChanged={handleSelectedDatesChanged}
      onEmployeeMissingZoomUserId={setEmployeeMissingZoomUserIds}
      onSelfScheduleCountError={setSelfScheduleCountError}
      shouldRespectLoadLimit={Boolean(options.shouldRespectLoadLimit)}
      canScheduleOverAvailableKeywords={Boolean(options.canScheduleOverAvailableKeywords)}
      canScheduleOverRecruitingKeywords={Boolean(options.canScheduleOverRecruitingKeywords)}
      canScheduleOverFreeTime={Boolean(options.canScheduleOverFreeTime)}
    />
  );

  const candidateEventAdditionalAttendees = options.candidateEventAdditionalAttendees
    ? {
        dynamicAudiences: options.candidateEventAdditionalAttendees.dynamicAudiences,
        externalEmailAddresses: options.candidateEventAdditionalAttendees.externalEmailAddresses,
        employeeIds: options.candidateEventAdditionalAttendees.employeeIds,
      }
    : null;
  const interviewerEventAdditionalAttendees = options.interviewerEventAdditionalAttendees
    ? {
        dynamicAudiences: options.interviewerEventAdditionalAttendees.dynamicAudiences,
        externalEmailAddresses: options.interviewerEventAdditionalAttendees.externalEmailAddresses,
        employeeIds: options.interviewerEventAdditionalAttendees.employeeIds,
      }
    : null;
  return (
    <DialogStep
      title={t('hiring-dashboard-schedule:schedule.selfScheduleOptions.addTimeframeTitle')}
      subTitle={
        !onBack
          ? t('hiring-dashboard-candidates:candidates.availabilityOptionsSetup.stepWithoutBack')
          : t('hiring-dashboard-candidates:candidates.availabilityOptionsSetup.stepWithBack')
      }
      submitOptions={{
        dataTestId: 'self-schedule-options-continue-button',
        isDisabled: !!disabledReason,
        tooltip: disabledReason,
        label: t('hiring-dashboard-candidates:candidates.availabilityOptionsSetup.continue'),
        onClick: onContinue,
      }}
      cancelOptions={
        !onBack
          ? {
              label: t('hiring-dashboard-common:common.cancel'),
              onClick: () => onClose(),
            }
          : {
              label: t('hiring-dashboard-candidates:candidates.availabilityOptionsSetup.previousStep'),
              onClick: onBack,
              startIcon: <CaretLeftIcon />,
            }
      }
      onClose={onClose}
    >
      <Stack direction="column" spacing={2}>
        <Stack
          direction="column"
          spacing={2}
          sx={{
            width: '100%',
          }}
        >
          {selfScheduleCountError && (
            <Alert data-testid="self-schedule-options-error-alert" severity="error">
              <AlertTitle>{selfScheduleCountError}</AlertTitle>
              {selfScheduleCountError}
            </Alert>
          )}

          {jobStageData?.jobStage?.name && (
            <Stack data-testid="self-schedule-options-interview-plan-description">
              <Typography fontWeight={600}>
                {t('hiring-dashboard-schedule:schedule.newSchedule.schedulingForStage', {
                  stageName: jobStageData.jobStage.name,
                })}
              </Typography>
              <Typography variant="caption" fontWeight={400} color="text.secondary">
                {interviewPlanDescription}
              </Typography>
            </Stack>
          )}
        </Stack>

        <ConfigurableSelfScheduleOptions
          alertJsx={
            <OptionsCountAlert
              text={t('hiring-dashboard-schedule:schedule.newSchedule.schedule')}
              enabled={suggestedOptionsOn ?? false}
              loading={loadingOptions}
              count={selectedDatesOptionsCount}
              error={selfScheduleCountError}
            />
          }
          title={t('hiring-dashboard-candidates:candidates.availabilityPreferences.preferences')}
          options={options}
          numberOfDays={numberOfDays}
          selectedDatesOptionsCount={selectedDatesOptionsCount}
          optionsCalendarJsx={optionsCalendarJsx}
          templates={templates}
          employeeMissingZoomUserIds={employeeMissingZoomUserIds}
          defaultTemplates={defaultTemplates}
          missingEmployeesData={missingEmployeesData}
          candidateEventAdditionalAttendees={candidateEventAdditionalAttendees}
          interviewerEventAdditionalAttendees={interviewerEventAdditionalAttendees}
          timeframeDays={timeframeDays}
          onRollingDaysChange={handleSetNumberOfDays}
          onTimeframeDaysChange={handleSetTimeframeDays}
          onRollingDaysToggle={(value: boolean) => {
            if (!value) {
              onOptionsChanged({
                ...options,
                rollingDays: null,
                inclusionDays: numberOfDays ? getInclusionDays(numberOfDays) : options.inclusionDays,
              });
            } else if (!options.rollingDays) {
              onOptionsChanged({
                ...options,
                rollingDays: numberOfDays || NumberOfDays.TwoWeeks,
                inclusionDays: undefined,
              });
            }
          }}
          onNoteChanged={(value: string) => {
            onOptionsChanged({ ...options, note: value });
          }}
          onOptionsChanged={onOptionsChanged}
          onCandidateEventAdditionalAttendeesChanged={(attendees) => {
            onOptionsChanged({ ...options, candidateEventAdditionalAttendees: attendees });
          }}
          onInterviewerEventAdditionalAttendeesChanged={(attendees) => {
            onOptionsChanged({ ...options, interviewerEventAdditionalAttendees: attendees });
          }}
          onTemplatesChanged={onTemplatesChanged}
          shouldSendPersonalizedEmail={shouldSendPersonalizedEmail}
          setShouldSendPersonalizedEmail={setShouldSendPersonalizedEmail}
          interviewMeetingLocation={interviewMeetingLocation}
          onInterviewMeetingLocationChanged={onInterviewMeetingLocationChanged}
        />
      </Stack>
    </DialogStep>
  );
};

SelfScheduleOptions.fragments = {
  options: gql`
    fragment SelfScheduleOptions_options on Options {
      inclusionDays
      rollingDays
      advanceNoticeInHours
      candidateCalendarId
      interviewerCalendarId
      location
      zoomHost
      zoomHostUserId
      candidateEmail
      shouldRespectLoadLimit
      canScheduleOverRecruitingKeywords
      canScheduleOverAvailableKeywords
      canScheduleOverFreeTime
      meetingHost
      videoMeetingHostEmployeeId
      note
      candidateEventAdditionalAttendees {
        employeeIds
        externalEmailAddresses
        dynamicAudiences
      }
      interviewerEventAdditionalAttendees {
        employeeIds
        externalEmailAddresses
        dynamicAudiences
      }
    }
  `,
  interviewPlan: gql`
    ${useInterviewPlanDescription.fragments.jobStage}
    fragment SelfScheduleOptions_interviewPlan on JobStage {
      id
      ...useInterviewPlanDescription_jobStage
    }
  `,
  interviewMeetingLocation: gql`
    ${ConfigurableSelfScheduleOptions.fragments.interviewMeetingLocation}
    fragment SelfScheduleOptions_interviewMeetingLocation on InterviewMeetingLocationSettingPref {
      ...ConfigurableSelfScheduleOptions_interviewMeetingLocation
    }
  `,
};

export default SelfScheduleOptions;
